import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import AcordoRenegociacao from '../shared/models/acordo-rengociacao.model';
import ContratoVendido from '../shared/models/contrato-vencido.model';
import CustomizacaoParcelamento from '../shared/models/customizacao-parcelamento.model';
import { ElegibilidadePagamentoCelerResponse } from '../shared/models/elegibilidade-pagamento-celer-response.model';
import { EnviaEmailBoletoRequest, EnvioDeEmailBoletoRenegociacoRequest } from '../shared/models/enviar-email-boleto-request.model';
import { EnviaSmsBoletoRequest, EnvioSmsBoletoRequest } from '../shared/models/enviar-sms-boleto-request.model';
import { FuncionalidadeEnum } from '../shared/models/funcionalidade-enum';
import { MapaPlanosPorOrigem } from '../shared/models/mapa-planos-por-origem.model';
import { NovoPlanoRenegociacao, OrigemPlanoEnum } from '../shared/models/novo-plano-renegociacao.model';
import { PlanoEfetivado } from '../shared/models/plano-efetivado.model';
import PropostaOutroCanal from '../shared/models/proposta-outro-canal.model';
import { ResumoRenegociacao } from '../shared/models/resumo-renegociacao.model';
import { ValidacaoCadastro } from '../shared/models/validacao-cadastro.model';
import BrowserUtils from '../shared/utils/browser-utils';
import StringUtils from '../shared/utils/string-utils';
import { AutenticacaoService } from './../seguranca/autenticacao.service';
import { ContratosApi } from './../shared/api/contratos.api';
import { Contrato } from './../shared/models/contrato.model';
import { PagamentoCeler, PagamentoCelerLinkResponse } from './../shared/models/pagamento-pix-celer-model';
import { Parcela } from './../shared/models/parcela.model';
import { EfetivarSegundaViaResponse } from './../shared/models/segunda-via-response.model';
import { ApiService } from '../shared/api/api.service';

export class SelectPlanNotFound extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SelectPlanNotFound';
    Object.setPrototypeOf(this, SelectPlanNotFound.prototype);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ContratoService {

  constructor(
    private api: ContratosApi,
    private autenticacaoService: AutenticacaoService,
    private http: HttpClient,
    private apiService: ApiService
  ) { }

  get apiUrlService(): string {
    if (BrowserUtils.isOldFirefox()) {
      return `${environment.cdccorpApiURLService}/v1`;
    }

    return `${environment.apiURLService}/v1`;
  }

  private contratos: Contrato[] = [];
  private planoEfetivado: PlanoEfetivado;

  private planos: MapaPlanosPorOrigem;

  private readonly CELER_PIX_PERMISSION_KEYS = {
    DOMINIO: 'PAGAMENTO_CELER',
    NOME: 'PIX'
  };

  iniciarFluxo(): void {
    this.contratos = [];
    this.planoEfetivado = null;
    this.planos = new MapaPlanosPorOrigem();
  }

  async getContratos(): Promise<Contrato[]> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/contratos',
      oldBrowserFallbackPath: '/contratos'
    })

    if (this.contratos.length === 0) {
      this.contratos = await this.http.get<Array<Contrato>>(path, {}).toPromise();
    }

    return this.contratos;
  }

  async getContrato(idContrato: string, refresh: boolean = false): Promise<Contrato | null> {
    if (refresh) {
      this.contratos = [];
    }
    const contratos = await this.getContratos();
    for (const contrato of contratos) {
      if (contrato.contrato === idContrato) {
        return contrato;
      }
    }
    return null;
  }

  async getParcela(idContrato: string, numeroParcela: number): Promise<Parcela | null> {
    const contrato = await this.getContrato(idContrato);
    if (contrato) {
      for (const parcela of contrato.parcelas) {
        if (parcela.numeroParcela === numeroParcela) {
          return parcela;
        }
      }
    }

    return null;
  }

  async getSegundaVia(idContrato: string, numeroParcela: number): Promise<EfetivarSegundaViaResponse> {
    return this.api.getSegundaVia(idContrato, numeroParcela).toPromise();
  }

  private base64toBlob(base64Data, contentType?): Blob {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  async downloadBoleto(idContrato: string, numeroParcela: number): Promise<void> {
    const base64Boleto = await this.api.getBoleto(idContrato, numeroParcela).toPromise();
    const element = document.createElement('a');
    document.body.appendChild(element);
    element.setAttribute('target', '_blank');
    element.setAttribute('href', URL.createObjectURL(this.base64toBlob(base64Boleto, 'application/pdf')));
    element.click();
    element.remove();
  }

  async downloadBoletoRenegociacao(idContrato: string): Promise<void> {
    const base64Boleto = await this.api.downloadBoletoRenegociacao(idContrato).toPromise();
    const element = document.createElement('a');
    document.body.appendChild(element);
    element.setAttribute('target', '_blank');
    element.setAttribute('href', URL.createObjectURL(this.base64toBlob(base64Boleto, 'application/pdf')));
    element.click();
    element.remove();
  }

  async downloadBoletoRenegociacaoMassiva(idContrato: string, isQuitacao: boolean): Promise<void> {
    const base64Boleto = await this.api.downloadBoletoRenegociacaoMassiva(idContrato, isQuitacao).toPromise();
    const element = document.createElement('a');
    document.body.appendChild(element);
    element.setAttribute('target', '_blank');
    element.setAttribute('href', URL.createObjectURL(this.base64toBlob(base64Boleto, 'application/pdf')));
    element.click();
    element.remove();
  }

  async downloadAcordoRenegociacaoPDF(acordoRenegociacao: AcordoRenegociacao) {
    const acordoContrato = await this.api.downloadAcordoRenegociacaoPDF(acordoRenegociacao).toPromise();
    const blob = new Blob([acordoContrato], { type: 'application/pdf' });
    const element = document.createElement('a');
    document.body.appendChild(element);
    element.setAttribute('target', '_blank');
    element.setAttribute('href', URL.createObjectURL(blob));
    element.click();
    element.remove();
  }

  async enviarBoletoEmailSegundaVia(enviarBoletoEmailRequest: EnviaEmailBoletoRequest): Promise<void> {
    return this.api.enviarBoletoEmailSegundaVia(enviarBoletoEmailRequest).toPromise();
  }

  async enviarBoletoSmsSegundaVia(enviaSmsBoletoRequest: EnviaSmsBoletoRequest): Promise<void> {
    return this.api.enviarBoletoSmsSegundaVia(enviaSmsBoletoRequest).toPromise();
  }

  async buscarPropostasOutrosCanais(idContrato: string): Promise<PropostaOutroCanal[]> {
    return this.api.getPropostasOutrosCanais(idContrato).toPromise();
  }

  async buscarContratosVendidos(): Promise<ContratoVendido[]> {
    return this.api.getContratosVendidos().toPromise();
  }

  async buscarNovosPlanos(contratoId: string): Promise<MapaPlanosPorOrigem> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/contratos/${contratoId}/renegociacoes/planos`,
      oldBrowserFallbackPath: `/contratos/${contratoId}/renegociacoes/planos`
    })

    this.planos = await this.http.get<NovoPlanoRenegociacao[]>(path)
      .pipe(map<NovoPlanoRenegociacao[], MapaPlanosPorOrigem>((res) => {
        const planosMap = new MapaPlanosPorOrigem();

        for (const plano of res) {
          const origem = plano.origem === OrigemPlanoEnum.CYBER ? OrigemPlanoEnum.CYBER : OrigemPlanoEnum.ESPECIAL;
          plano.id = StringUtils.generateUniqueId();
          planosMap[origem][plano.tipo].push(plano);
        }
        return planosMap;
      }))
      .toPromise();

    return this.planos;
  }

  buscarNovoPlanoPeloId(id: string): NovoPlanoRenegociacao {
    if (!this.planos) {
      throw new SelectPlanNotFound('Plano não encontrado');
    }

    const planos = Object.keys(this.planos).reduce((prev, key) => [...prev, ...this.planos[key].PARCELADO, ...this.planos[key].QUITACAO], []);

    if (planos.length > 0) {
      const [novoPlano] = planos.filter((plano) => plano.id === id);
      return novoPlano;
    }

    return null;
  }

  async buscarResumoRenegociacao(contratoId: string, plano: NovoPlanoRenegociacao): Promise<ResumoRenegociacao> {
    const { id, ...planoSemId } = plano;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/contratos/${contratoId}/renegociacoes/plano/resumo`,
      oldBrowserFallbackPath: `/contratos/${contratoId}/renegociacoes/plano/resumo`
    })

    return this.http.post<ResumoRenegociacao>(path, planoSemId).toPromise();
  }

  async efetivarAcordo(contratoId: string, plano: NovoPlanoRenegociacao): Promise<any> {
    const { id, ...planoParaSalvar } = plano;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/contratos/${contratoId}/renegociacoes/plano/efetivar`,
      oldBrowserFallbackPath: `/contratos/${contratoId}/renegociacoes/plano/efetivar`
    })

    return this.http.post(path, planoParaSalvar).toPromise();
  }

  async buscarPlanoEfetivado(contratoId: string): Promise<PlanoEfetivado> {
    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/contratos/${contratoId}/renegociacoes/plano/efetivado`,
      oldBrowserFallbackPath: `/contratos/${contratoId}/renegociacoes/plano/efetivado`
    })

    this.planoEfetivado = await this.http.get<PlanoEfetivado>(path).toPromise();

    return this.planoEfetivado;
  }

  async recalcularPlanos(contratoId: string, customizacaoParcelamento: CustomizacaoParcelamento) {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/contratos/${contratoId}/renegociacoes/planos/recalcular`,
      oldBrowserFallbackPath: `/contratos/${contratoId}/renegociacoes/planos/recalcular`
    })

    let params = new HttpParams();

    params = params.set('entrada', customizacaoParcelamento.valorEntrada.toString());
    params = params.set('data', format(customizacaoParcelamento.dataVencimento, 'yyyy-MM-dd'));

    const response = await this.http.get<NovoPlanoRenegociacao[]>(path, { params }).toPromise();

    this.planos.CYBER.PARCELADO = [];
    this.planos.CYBER.QUITACAO = [];
    for (const plano of response) {
      plano.id = StringUtils.generateUniqueId();
      this.planos.CYBER[plano.tipo].push(plano);
    }

    return this.planos;
  }

  async downloadBoletoNovoMassivo(contratoId: string) {
    const url = `${this.apiUrlService}/contratos/portal/contratos/${contratoId}/renegociacoes/plano/efetivado/boleto/pdf`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/contratos/${contratoId}/renegociacoes/plano/efetivado/boleto/pdf`,
      oldBrowserFallbackPath: `/portal/contratos/${contratoId}/renegociacoes/plano/efetivado/boleto/pdf`
    })

    const boleto = await this.http.get(path, { responseType: 'arraybuffer' }).toPromise();

    const blob = new Blob([boleto], { type: 'application/pdf' });
    const element = document.createElement('a');
    document.body.appendChild(element);
    element.setAttribute('target', '_blank');
    element.setAttribute('href', URL.createObjectURL(blob));
    element.click();
    element.remove();
  }

  async enviarBoletoEmailRenegociacao(enviarBoletoEmailRequest: EnvioDeEmailBoletoRenegociacoRequest): Promise<void> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/contratos/renegociacoes/boletos/enviar`,
      oldBrowserFallbackPath: `/contratos/renegociacoes/boletos/enviar`
    })

    return this.http.post<void>(path, enviarBoletoEmailRequest).toPromise();
  }

  async enviarBoletoSmsRenegociacao(enviaSmsBoletoRequest: EnvioSmsBoletoRequest): Promise<void> {
    const data: EnvioSmsBoletoRequest = { ...enviaSmsBoletoRequest, phone: `55${enviaSmsBoletoRequest.phone}` };

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/renegociacoes/enviar/linha-digitavel`,
      oldBrowserFallbackPath: `/portal/renegociacoes/enviar/linha-digitavel`
    })

    return this.http.post<void>(path, data).toPromise();
  }

  async buscarLinkPagamentoPix(informacaoPagamento: PagamentoCeler): Promise<PagamentoCelerLinkResponse> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/pagamentos/link',
      oldBrowserFallbackPath: '/pagamentos/link'
    })
    return this.http.post<PagamentoCelerLinkResponse>(path, informacaoPagamento).toPromise();
  }

  async verificarElegibilidadePagamentoCeler(contratoId: string): Promise<ElegibilidadePagamentoCelerResponse> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/pagamentos/${contratoId}/elegibilidade`,
      oldBrowserFallbackPath: `/pagamentos/${contratoId}/elegibilidade`
    })
    return this.http.get<ElegibilidadePagamentoCelerResponse>(path).toPromise();
  }

  async validarCadastro(funcionalidade: FuncionalidadeEnum): Promise<ValidacaoCadastro> {
    const funcionalidadePath = funcionalidade === FuncionalidadeEnum.RENEGOCIACAO ? 'renegociacao' : 'parcela';

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/clientes/validacao-cadastro/${funcionalidadePath}`,
      oldBrowserFallbackPath: `/clientes/validacao-cadastro/${funcionalidadePath}`
    })
    return this.http.get<ValidacaoCadastro>(path).toPromise();
  }

}
